import { AfterViewInit, Component, ElementRef, HostListener, Input, ViewChild } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { GameType, GetMagicCardRequest, GetMagicCardResponse, GetMagicSetResponse, MagicSet, ViaSet } from "@apiserver-rest/ponder-data-client-base";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { addSegmentToList, intializeBaseSegmentArray } from "@ponder-features/header/navbar/navbar.component";
import { debounceTime, Subscription, take } from "rxjs";
import { FilteredPreviewItems } from "@ponder-shared/tools/filter/filtered-preview-items";
import { PreviewItemFilters } from "@ponder-shared/tools/filter/preview-item-filters";
import { MagicSetInfoComponent } from "./magic-set-info.component";
import { LoadingComponent } from "@ponder-shared/loading/loading.component";
import { VirtualGridComponent } from "@ponder-shared/virtual/virtual-grid.component";
import { SmoothSlider } from "@ponder-shared/utils/smoothSlider";

@Component ({
    imports: [LoadingComponent, MagicSetInfoComponent, VirtualGridComponent],
    templateUrl: "./magic-set.component.html"
})
export class MagicSetComponent implements AfterViewInit {
    @Input() public setCode!: string;
    
    @ViewChild("setContentsContainer") private setContentsContainerRef!: ElementRef<HTMLElement>;
    @ViewChild("setInfoContainer") private setInfoContainerRef!: ElementRef<HTMLElement>;

    private smoothSlider?: SmoothSlider;

    public isLoading = true;
    public loadingSuccess = false;

    public magicSet?: MagicSet;
    public childSets?: MagicSet[];

    public previewItems?: FilteredPreviewItems;
    private filterChangeSub?: Subscription;

    public constructor(
        private ponderDataClient: PonderDataClient,
        private pageTitle: Title,
        private events: EventBroker) { 
            this.filterChangeSub = this.events.ui.previewFilterChangeEvent
                .pipe(debounceTime(100))
                .subscribe({
                    next: (filter: PreviewItemFilters) => {
                        this.onFilterUpdated(filter);
                    }
                });
        }

    public ngOnDestroy(): void {
        this.filterChangeSub?.unsubscribe();
    }

    /**
     * Triggered when the item filter (sorting, color only, etc.) has been modified.
     * @param itemFilter
     */
    private onFilterUpdated(itemFilter: PreviewItemFilters) {
        this.previewItems?.populate(itemFilter);
        window.scroll(0, 0);
    }

    public ngAfterViewInit(): void {
        this.smoothSlider = new SmoothSlider(this.setInfoContainerRef.nativeElement, this.setContentsContainerRef.nativeElement, 119, 250);
        this.setCode = this.setCode.toLowerCase();

        // Get the set
        this.ponderDataClient
            .magicSet(this.setCode)
            .pipe(take(1))
            .subscribe({
                next: (response: GetMagicSetResponse) => {
                    if (response.success) {
                        this.loadingSuccess = true;
                        this.magicSet = response.set!;
                        this.childSets = response.children;
                        this.pageTitle.setTitle(`Ponder > ${this.magicSet.name} (${this.magicSet.code!.toUpperCase()})`);
                    }

                    this.sendNavbarUpdate();
                },
                error: () => {
                    this.sendNavbarUpdate();
                }
            })
            .add(() => { this.isLoading = false; });

        // Get the items
        this.ponderDataClient
            .magicCard(new GetMagicCardRequest({
                preview: true,
                setProps: new ViaSet({ 
                    setCode: this.setCode
                })
            }))
            .pipe(take(1))
            .subscribe({
                next: (response: GetMagicCardResponse) => {
                    if (response.success && response.previews) {
                        this.previewItems = new FilteredPreviewItems(response.previews, this.events);
                        this.previewItems.populate();

                        this.events.ui.toggleSortFilterBarEvent.emit(true);
                        this.events.ui.previewItemsFetchedEvent.emit(this.previewItems);
                    }
                }
            });
    }

    /**
     * Updates the navbar / breadcrumbs for the current set and its parent set and/or block.
     */
    private sendNavbarUpdate(): void {
        var segments = intializeBaseSegmentArray(GameType.Magic);

        if (!this.magicSet || !segments) {
            this.events.ui.navbarUpdateEvent.emit();
            return;
        }
        
        if (!this.magicSet.parentSet) {
            addSegmentToList(segments, this.magicSet.block?.name, this.magicSet.block?.code?.toUpperCase(), `/magic/block/${this.magicSet.block?.code}`);
        } else {
            addSegmentToList(segments, this.magicSet.parentSet?.name, this.magicSet.parentSet?.code?.toUpperCase(), `/magic/set/${this.magicSet.parentSet?.code}`);
        }
        
        addSegmentToList(segments, this.magicSet.name, this.magicSet.name);

        this.events.ui.navbarUpdateEvent.emit(segments);
    }
    
    @HostListener("window:scroll", ["$event"])
    @HostListener('window:resize', ['$event'])
    private onWindowChange(): void {
        this.smoothSlider?.onWindowChange();
    }
}